import { useState, useEffect, useCallback, forwardRef } from 'react';

import SCANNER_SOUND from '@assets/audio/scan-beep.mp3';

import { useQrReader } from '@features/Scan/hooks/useQrReader';

import CommonImage from '@/components/CommonImage';

const successAudio = new Audio(SCANNER_SOUND);

const FlashlightIcon = ({ isOn, className, ...props }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="24"
		height="24"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className={className}
		{...props}
	>
		{!isOn ? (
			<>
				<path d="M18 6c0 2-2 2-2 4v10a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V10c0-2-2-2-2-4V2h12z" />
				<line x1="6" x2="18" y1="6" y2="6" />
				<line x1="12" x2="12" y1="12" y2="12" />
			</>
		) : (
			<>
				<path d="M16 16v4a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2V10c0-2-2-2-2-4" />
				<path d="M7 2h11v4c0 2-2 2-2 4v1" />
				<line x1="11" x2="18" y1="6" y2="6" />
				<line x1="2" x2="22" y1="2" y2="22" />
			</>
		)}
	</svg>
);

const ScanWithCamera = forwardRef(
	({ videoId = 'video', onClose, onGetQrCannedResult, setCapturedImageSrc }, ref) => {
		const [error, setError] = useState(null);
		const [isPaused, setIsPaused] = useState(false);
		const [torch, setTorch] = useState(false);
		const [zoom, setZoom] = useState(1);
		const [cameraCapabilities, setCameraCapabilities] = useState({
			hasZoom: false,
			zoomRange: { min: 1, max: 8 }
		});

		const { stop: stopScanning, reset: resetScanning } = useQrReader({
			constraints: {
				video: {
					facingMode: 'environment',
					width: { ideal: 1280 },
					height: { ideal: 720 },
					frameRate: { ideal: 30 },
					aspectRatio: { ideal: 1.777778 },
					focusMode: 'continuous',
					whiteBalanceMode: 'continuous'
				}
			},
			scanDelay: 100,
			onResult: async (result) => {
				if (!result || isPaused) return;

				const newValue = result.getText();
				if (newValue && newValue.length > 4) {
					setIsPaused(true);
					captureFrame(newValue);
				}
			},
			videoId,
			setError,
			isPaused
		});

		const getMediaStream = async () => {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					video: { facingMode: 'environment' },
					audio: false
				});
				return stream.getVideoTracks()[0];
			} catch {
				setError('Failed to access camera');
				return null;
			}
		};

		const toggleTorch = async () => {
			try {
				const track = await getMediaStream();
				if (!track) return;

				const capabilities = track.getCapabilities();
				if (!capabilities.torch) {
					throw new Error('Torch not supported');
				}

				await track.applyConstraints({
					advanced: [{ torch: !torch }]
				});
				setTorch(!torch);
			} catch {
				setError('Torch is not supported on this device');
			}
		};

		const handleZoom = async (zoomLevel) => {
			try {
				const track = await getMediaStream();
				if (!track || !cameraCapabilities.hasZoom) return;

				const { min, max } = cameraCapabilities.zoomRange;
				const newZoom = Math.max(min, Math.min(max, zoomLevel));

				await track.applyConstraints({
					advanced: [{ zoom: newZoom }]
				});
				setZoom(newZoom);
			} catch {
				setError('Zoom adjustment failed');
			}
		};

		const checkCameraCapabilities = useCallback(async () => {
			const track = await getMediaStream();
			if (!track) return;

			const capabilities = track.getCapabilities();
			setCameraCapabilities({
				hasZoom: !!capabilities.zoom,
				zoomRange: capabilities.zoom
					? { min: capabilities.zoom.min, max: capabilities.zoom.max }
					: { min: 1, max: 8 }
			});
		}, []);

		useEffect(() => {
			checkCameraCapabilities();
		}, [checkCameraCapabilities]);

		const captureFrame = useCallback(
			(scannedValue) => {
				const videoElement = document.getElementById(videoId);
				if (!videoElement || videoElement.readyState !== 4) return;

				try {
					onGetQrCannedResult(scannedValue);
					const canvas = document.createElement('canvas');
					canvas.width = videoElement.videoWidth;
					canvas.height = videoElement.videoHeight;
					canvas.getContext('2d').drawImage(videoElement, 0, 0);

					const imageSrc = canvas.toDataURL('image/png');
					setCapturedImageSrc(imageSrc);
					displayCapturedFrame(imageSrc, videoElement);

					stopScanning();
					successAudio.play();
				} catch {
					setError('Failed to capture frame');
				}
			},
			[videoId, stopScanning, setCapturedImageSrc, onGetQrCannedResult]
		);

		const displayCapturedFrame = (imageSrc, videoElement) => {
			const img = document.createElement('img');
			img.src = imageSrc;
			img.id = 'captured-frame';
			Object.assign(img.style, {
				width: '100%',
				height: '100%',
				objectFit: 'cover',
				position: 'absolute',
				top: '0',
				left: '0',
				zIndex: '1'
			});

			document.getElementById('captured-frame')?.remove();

			// Safely insert the image element
			const parentElement = videoElement.parentElement;
			if (parentElement && parentElement.contains(videoElement)) {
				const nextSibling = videoElement.nextSibling;
				if (nextSibling && parentElement.contains(nextSibling)) {
					parentElement.insertBefore(img, nextSibling);
				} else {
					parentElement.appendChild(img);
				}
			}

			videoElement.style.opacity = '0';
		};

		const handleScanAgain = useCallback(() => {
			stopScanning();
			setIsPaused(false);
			setCapturedImageSrc(null);

			document.getElementById('captured-frame')?.remove();
			const videoElement = document.getElementById(videoId);
			if (videoElement) videoElement.style.opacity = '1';

			setTimeout(resetScanning, 50);
		}, [stopScanning, resetScanning, videoId, setCapturedImageSrc]);

		useEffect(() => {
			if (ref) ref.current = handleScanAgain;
		}, [ref, handleScanAgain]);

		useEffect(() => {
			if (error) {
				const timer = setTimeout(() => setError(null), 1500);
				return () => clearTimeout(timer);
			}
		}, [error]);

		useEffect(() => {
			return () => {
				document.getElementById('captured-frame')?.remove();
				stopScanning();
			};
		}, [stopScanning]);

		return (
			<div className="qr-scanner d-flex align-items-center justify-content-center flex-column mb-3">
				{error && (
					<div
						className="error-container"
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							zIndex: 9,
							textAlign: 'center'
						}}
					>
						<p style={{ color: '#f22b2b' }}>{error}</p>
					</div>
				)}
				<div className="close-icon">
					<CommonImage
						src="/icons/close-14.svg"
						width={24}
						height={24}
						className="cursor-pointer z-index-10"
						onClick={onClose}
					/>
				</div>
				<video
					muted
					id={videoId}
					aria-label="QR code scanner viewport"
					disablepictureinpicture
					playsinline
				></video>
				<div className="qr-img after-before-content d-flex align-items-center justify-content-center flex-column">
					<span className="qr-border_btm after-before-content"></span>
				</div>
				<div className="qr-scanner-bottom d-flex align-items-center justify-content-between">
					<div className="light-btn">
						<FlashlightIcon
							isOn={torch}
							className="cursor-pointer"
							onClick={toggleTorch}
							style={{ width: 27, height: 27, color: '#fff' }}
						/>
					</div>
					<div className="zoom-in-out-btn">
						<CommonImage
							src="/icons/add-round.svg"
							className="cursor-pointer add-icon"
							width={24}
							height={24}
							onClick={() => handleZoom(zoom + 1)}
						/>
						<CommonImage
							src="/icons/minus-round.svg"
							className="cursor-pointer"
							width={24}
							height={24}
							onClick={() => handleZoom(zoom - 1)}
						/>
					</div>
				</div>
			</div>
		);
	}
);

export default ScanWithCamera;
